import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";
import { getStatusLabel } from "~/pages/purchase-orders/utils/statusLabels";
import { facturaCostsService } from "~/services/facturaCostsService";
import { SalesCondition, SalesRequest } from "~/types/client";
import { calcLineTotal } from "~/utils/lineItemCalculations";

interface ExportSalesRequestsOptions {
  salesRequests: SalesRequest[];
  clientName: string;
  getConditionById: (id: string | undefined) => SalesCondition | null;
  clientSalesConditionId?: string;
}

/**
 * Credit-note totals per sales request, mirroring the aggregation in
 * SalesRequestsTable (abs of net + tax, falling back to total_amount).
 */
const fetchCreditNoteTotals = async (
  salesRequestIds: string[]
): Promise<Record<string, number>> => {
  const totals: Record<string, number> = {};
  if (salesRequestIds.length === 0) return totals;

  const { data, error } = await supabase
    .from("sales_request_documents")
    .select("sales_request_id, net_amount, tax_amount, total_amount")
    .eq("document_type", "nota_de_credito")
    .in("sales_request_id", salesRequestIds)
    .is("deleted_at", null);

  if (error) throw error;

  (data ?? []).forEach((note) => {
    const amount =
      (note.net_amount || 0) + (note.tax_amount || 0) || note.total_amount || 0;
    const id = note.sales_request_id as string;
    totals[id] = (totals[id] || 0) + Math.abs(amount);
  });

  return totals;
};

/** Line item with its product join, as returned by the detail fetch below. */
interface LineItemRow {
  id: string;
  sales_request_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number | null;
  product_name_snapshot: string | null;
  products: {
    short_name: string | null;
    long_name: string | null;
    sku: string | null;
    barcode: string | null;
    internal_id: string | null;
    unit_code: string | null;
  } | null;
}

/**
 * Product lines per sales request (non-deleted), with the product code/name
 * needed for the "Detalle por Producto" sheet. Batched to respect PostgREST
 * `in()` limits. Keyed by `sales_request_id`.
 */
const fetchLineItems = async (
  salesRequestIds: string[]
): Promise<Map<string, LineItemRow[]>> => {
  const map = new Map<string, LineItemRow[]>();
  if (salesRequestIds.length === 0) return map;

  const BATCH_SIZE = 100;
  for (let i = 0; i < salesRequestIds.length; i += BATCH_SIZE) {
    const batch = salesRequestIds.slice(i, i + BATCH_SIZE);
    const { data, error } = await supabase
      .from("sales_request_items")
      .select(
        `
        id,
        sales_request_id,
        quantity,
        unit_price,
        discount_percent,
        product_name_snapshot,
        products:product_id (
          short_name,
          long_name,
          sku,
          barcode,
          internal_id,
          unit_code
        )
      `
      )
      .in("sales_request_id", batch)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });

    if (error) throw error;

    (data ?? []).forEach((row) => {
      const item = row as unknown as LineItemRow;
      const list = map.get(item.sales_request_id) ?? [];
      list.push(item);
      map.set(item.sales_request_id, list);
    });
  }

  return map;
};

/**
 * Export the given sales notes to an .xlsx workbook with two sheets:
 *  - "Notas de Venta": one row per note (header-level summary, matching the
 *    columns shown in SalesRequestsTable).
 *  - "Detalle por Producto": one row per product line, with código, producto,
 *    cantidad, precio unitario y costo/margen (WAC neto; en blanco cuando la
 *    línea aún no tiene costeo). Permite ver a qué precio se vendió cada ítem.
 * Amounts are written as raw numbers so Excel can sort/sum them. Returns the
 * generated filename plus note and line-item counts.
 */
export async function exportSalesRequestsToExcel({
  salesRequests,
  clientName,
  getConditionById,
  clientSalesConditionId,
}: ExportSalesRequestsOptions) {
  const salesRequestIds = salesRequests.map((sr) => sr.id);

  const [creditNoteTotals, itemsByRequest] = await Promise.all([
    fetchCreditNoteTotals(salesRequestIds),
    fetchLineItems(salesRequestIds),
  ]);

  // Per-line WAC unit cost (net CLP), absent when the line is not yet costed.
  const allItemIds = Array.from(itemsByRequest.values())
    .flat()
    .map((it) => it.id);
  const lineCostMap = await facturaCostsService.getLineCostsByItemIds(
    allItemIds
  );

  const resolveCondition = (sr: SalesRequest): string => {
    const isInherited = !sr.salesConditionId;
    const conditionId = sr.salesConditionId || clientSalesConditionId;
    const condition = conditionId ? getConditionById(conditionId) : null;
    if (!condition) return "-";
    return isInherited ? `${condition.name} (heredada)` : condition.name;
  };

  // --- Sheet 1: header-level summary (unchanged) ---
  const summaryRows = salesRequests.map((sr) => ({
    "N° Factura / ID": sr.factura_number || sr.id,
    Fecha: sr.createdAt ? dayjs(sr.createdAt).format("DD/MM/YYYY") : "",
    Monto: sr.price_with_iva || sr.totalAmount || 0,
    Condición: resolveCondition(sr),
    Estado: getStatusLabel(sr.status),
    Pago: sr.paid ? "Pagado" : "Pendiente",
    "Nota Crédito": creditNoteTotals[sr.id] || 0,
    Tipo: sr.paymentType === "prepaid" ? "Prepago" : "Crédito",
  }));

  // --- Sheet 2: product-line detail ---
  const detailRows: Record<string, string | number>[] = [];
  salesRequests.forEach((sr) => {
    const items = itemsByRequest.get(sr.id) ?? [];
    if (items.length === 0) return;

    const facturaId = sr.factura_number || sr.id;
    const fecha = sr.createdAt ? dayjs(sr.createdAt).format("DD/MM/YYYY") : "";
    const estado = getStatusLabel(sr.status);
    const tipo = sr.paymentType === "prepaid" ? "Prepago" : "Crédito";

    items.forEach((item) => {
      const product = item.products;
      const codigo =
        product?.sku || product?.barcode || product?.internal_id || "-";
      const nombre =
        item.product_name_snapshot ||
        product?.long_name ||
        product?.short_name ||
        "-";
      const discount = item.discount_percent ?? 0;
      const totalLinea = calcLineTotal(item.unit_price, item.quantity, discount);

      const unitCost = lineCostMap.get(item.id);
      const hasCost = unitCost !== undefined && unitCost > 0;
      const costoTotal = hasCost ? Math.round(unitCost! * item.quantity) : null;
      const margen = costoTotal !== null ? totalLinea - costoTotal : null;
      const margenPct =
        margen !== null && totalLinea !== 0
          ? Math.round((margen / totalLinea) * 1000) / 10
          : null;

      detailRows.push({
        "N° Factura / ID": facturaId,
        Fecha: fecha,
        Código: codigo,
        Producto: nombre,
        Unidad: product?.unit_code || "",
        Cantidad: item.quantity,
        "Precio Unitario": item.unit_price,
        "Descuento %": discount || 0,
        "Total Línea": totalLinea,
        "Costo Unitario": hasCost ? Math.round(unitCost!) : "",
        "Costo Total": costoTotal ?? "",
        Margen: margen ?? "",
        "% Margen": margenPct ?? "",
        Estado: estado,
        Tipo: tipo,
      });
    });
  });

  const wb = XLSX.utils.book_new();

  const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
  summaryWs["!cols"] = [
    { wch: 20 },
    { wch: 12 },
    { wch: 14 },
    { wch: 28 },
    { wch: 22 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Notas de Venta");

  if (detailRows.length > 0) {
    const detailWs = XLSX.utils.json_to_sheet(detailRows);
    detailWs["!cols"] = [
      { wch: 20 }, // N° Factura / ID
      { wch: 12 }, // Fecha
      { wch: 14 }, // Código
      { wch: 40 }, // Producto
      { wch: 10 }, // Unidad
      { wch: 10 }, // Cantidad
      { wch: 14 }, // Precio Unitario
      { wch: 12 }, // Descuento %
      { wch: 14 }, // Total Línea
      { wch: 14 }, // Costo Unitario
      { wch: 14 }, // Costo Total
      { wch: 14 }, // Margen
      { wch: 10 }, // % Margen
      { wch: 22 }, // Estado
      { wch: 10 }, // Tipo
    ];
    XLSX.utils.book_append_sheet(wb, detailWs, "Detalle por Producto");
  }

  const safeClient =
    (clientName || "cliente")
      .replace(/[^\p{L}\p{N}]+/gu, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase() || "cliente";
  const filename = `notas_de_venta_${safeClient}_${dayjs().format(
    "YYYY-MM-DD"
  )}.xlsx`;
  XLSX.writeFile(wb, filename);

  return { filename, count: summaryRows.length, detailCount: detailRows.length };
}
